/**
 * Procedural gameplay ambient — zero-byte background pad generated entirely
 * via the Web Audio API.
 *
 * Design goals (per the spec):
 *   - Lightweight: no audio files, ~3 oscillators + 1 filter + 1 LFO.
 *   - Won't obstruct gameplay: master gain held at ~0.06 (very quiet),
 *     lowpass-filtered to roll off any harsh harmonics.
 *   - Muted by default. Browsers BLOCK autoplay without a user gesture
 *     anyway, so unmuted-on-load just produces silent confusion. The
 *     speaker toggle in the appbar is the explicit gesture.
 *   - Mute choice persists across sessions via localStorage.
 *
 * Singleton — the same audio context lives across page navigations within
 * a session, so toggling mute from one game persists into the next.
 */

const STORAGE_KEY = 'triviana.audio.mute';
const FADE_MS = 800;
const CHORD_INTERVAL_MS = 6000;
const TARGET_GAIN = 0.06;

// Soft chord progression — gentle Am → F → C → G feel. Frequencies are
// the chord tones; voicing kept in the lower midrange so it doesn't
// fight the speaker output of a phone.
const CHORDS = [
  [220.00, 261.63, 329.63], // A3, C4, E4   — Am
  [174.61, 220.00, 261.63], // F3, A3, C4   — F
  [196.00, 246.94, 329.63], // G3, B3, E4   — G/B
  [261.63, 329.63, 392.00], // C4, E4, G4   — C
];

class GameAudio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this._lpf = null;
    this._lfo = null;
    this._voices = [];
    this._chordTimer = null;
    this._chordIdx = 0;
    this._wantsPlaying = false;       // matches what start()/stop() requested
    this._muted = this._readMutePref();
    // Listeners get notified when mute state changes so toggles re-render.
    this._listeners = new Set();
  }

  // --- public API ---------------------------------------------------------

  isMuted() { return this._muted; }

  toggleMute() {
    this._muted = !this._muted;
    try { localStorage.setItem(STORAGE_KEY, this._muted ? '1' : '0'); } catch { /* sandboxed */ }
    if (this._muted) {
      this._fadeOut();
    } else if (this._wantsPlaying) {
      // Unmuting mid-game: kick the engine into life.
      this._spinUp();
    }
    this._notify();
    return this._muted;
  }

  /** Begin playing if the user has unmuted. No-op when muted. */
  async start() {
    this._wantsPlaying = true;
    if (this._muted) return;
    await this._spinUp();
  }

  /** Stop playing immediately (fade out + tear down). Safe to call any time. */
  stop() {
    this._wantsPlaying = false;
    if (this._chordTimer) { clearInterval(this._chordTimer); this._chordTimer = null; }
    this._fadeOut();
    // Tear down a tick after the fade ends so we don't leak the AudioContext.
    setTimeout(() => this._teardown(), FADE_MS + 100);
  }

  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  // --- internals ----------------------------------------------------------

  _readMutePref() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === null) return true;  // default muted
      return raw === '1';
    } catch { return true; }
  }

  _notify() {
    for (const fn of this._listeners) {
      try { fn(this._muted); } catch (e) { console.error('audio listener', e); }
    }
  }

  async _spinUp() {
    await this._ensureContext();
    this._fadeIn();
    if (!this._chordTimer) this._beginChordRotation();
  }

  async _ensureContext() {
    if (this.ctx && this.ctx.state !== 'closed') {
      if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => {});
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;                    // SSR / very old browser
    this.ctx = new AC();
    if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => {});

    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(this.ctx.destination);

    // Warm lowpass — kills any high-end fizz so the pad sits behind the UI.
    this._lpf = this.ctx.createBiquadFilter();
    this._lpf.type = 'lowpass';
    this._lpf.frequency.value = 1200;
    this._lpf.Q.value = 0.4;
    this._lpf.connect(this.master);

    // Slow LFO on the filter cutoff for subtle movement.
    this._lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    this._lfo.frequency.value = 0.08;   // ~12 sec cycle
    lfoGain.gain.value = 250;
    this._lfo.connect(lfoGain).connect(this._lpf.frequency);
    this._lfo.start();
  }

  _fadeIn() {
    if (!this.master || !this.ctx) return;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value || 0, now);
    this.master.gain.linearRampToValueAtTime(TARGET_GAIN, now + FADE_MS / 1000);
  }

  _fadeOut() {
    if (!this.master || !this.ctx) return;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(0, now + FADE_MS / 1000);
  }

  _beginChordRotation() {
    this._playChord(CHORDS[this._chordIdx]);
    this._chordTimer = setInterval(() => {
      this._chordIdx = (this._chordIdx + 1) % CHORDS.length;
      this._playChord(CHORDS[this._chordIdx]);
    }, CHORD_INTERVAL_MS);
  }

  _playChord(freqs) {
    if (!this.ctx || !this._lpf) return;
    const now = this.ctx.currentTime;
    const fadeSec = 1.5;
    // Fade out the previous voices and stop them once silent.
    for (const v of this._voices) {
      v.gain.gain.cancelScheduledValues(now);
      v.gain.gain.setValueAtTime(v.gain.gain.value, now);
      v.gain.gain.linearRampToValueAtTime(0, now + fadeSec);
      try { v.osc.stop(now + fadeSec + 0.05); } catch { /* already stopped */ }
    }
    this._voices = [];
    // Spin up the new chord. Tiny per-voice detune gives a chorus-like
    // shimmer without sounding glitchy.
    const perVoiceGain = 1 / Math.max(1, freqs.length);
    for (const f of freqs) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = f;
      osc.detune.value = (Math.random() - 0.5) * 8;
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(this._lpf);
      osc.start(now);
      gain.gain.linearRampToValueAtTime(perVoiceGain, now + fadeSec);
      this._voices.push({ osc, gain });
    }
  }

  _teardown() {
    if (this._wantsPlaying) return;     // started again before teardown — keep alive
    for (const v of this._voices) {
      try { v.osc.stop(); } catch { /* fine */ }
    }
    this._voices = [];
    try { this._lfo?.stop(); } catch { /* fine */ }
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close().catch(() => {});
    }
    this.ctx = null;
    this.master = null;
    this._lpf = null;
    this._lfo = null;
  }
}

export const gameAudio = new GameAudio();

/**
 * Inject a speaker-icon button into the supplied container (an appbar).
 * Returns a detach() function the page calls on unmount.
 *
 * The button is the user's gesture that unlocks the AudioContext on
 * mobile browsers — first tap to unmute kicks audio on for the rest
 * of the session.
 */
export function mountAudioToggle(container) {
  if (!container) return () => {};
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'audio-toggle';
  btn.style.cssText =
    'background:none;border:none;color:#fff;font-size:18px;padding:6px 10px;cursor:pointer;line-height:1;opacity:0.85;';
  btn.setAttribute('aria-label', 'Toggle gameplay sound');
  const render = (muted) => {
    btn.textContent = muted ? '🔇' : '🔊';
    btn.title = muted ? 'Sound off — tap to enable' : 'Sound on — tap to mute';
  };
  render(gameAudio.isMuted());
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    gameAudio.toggleMute();
  });
  const unsubscribe = gameAudio.onChange(render);
  container.appendChild(btn);
  return () => {
    unsubscribe();
    btn.remove();
  };
}
